import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  buildCategoryTree,
  hasCategoryProducts,
  CategoryWithChildren,
} from "../../helpers/productsCategoryHelpers";
import type { FilterState } from "../../productSlice";
import type {
  TransformedWPCategory,
  TransformedWPTag,
  TransformedWPBrand,
} from "../../types/productTaxonomyApiTypes";

interface FilterSidebarProps {
  idPrefix: string;
  categories: TransformedWPCategory[];
  tags: TransformedWPTag[];
  brands: TransformedWPBrand[];
  activeFilters: FilterState;
  onCategoryClick: (categoryId: number | null) => void;
  onTagClick: (tagId: number) => void;
  onBrandClick: (brandSlug: string) => void;
  onPriceFilter: ({
    minPrice,
    maxPrice,
  }: {
    minPrice: number | null;
    maxPrice: number | null;
  }) => void;
  onSearchFilter: (searchQuery: string) => void;
  onSaleToggle: (checked: boolean) => void;
  isLoading?: boolean;
  onClose?: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  idPrefix,
  categories,
  tags,
  brands,
  activeFilters,
  onCategoryClick,
  onTagClick,
  onBrandClick,
  /*   onPriceFilter, */
  onSearchFilter,
  /*   onSaleToggle, */
  isLoading = false,
  onClose,
}) => {
  const { t } = useTranslation("common");
  const [expandedCategories, setExpandedCategories] = useState<
    Record<number, boolean>
  >({});
  const [searchValue, setSearchValue] = useState<string>(
    activeFilters.search || ""
  );

  useEffect(() => {
    setSearchValue(activeFilters.search || "");
  }, [activeFilters.search]);

  const categoryTree = useMemo(
    () => buildCategoryTree(categories),
    [categories]
  );

  const topTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) {
      return [];
    }
    return [...tags].sort((a, b) => b.count - a.count).slice(0, 8);
  }, [tags]);

  const toggleCategory = (categoryId: number): void => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const isCategoryActive = (id: number): boolean => {
    return activeFilters.category.includes(id);
  };

  const hasActiveChild = (category: CategoryWithChildren): boolean => {
    if (isCategoryActive(category.id)) return true;
    if (category.children && category.children.length > 0) {
      return category.children.some((child) => hasActiveChild(child));
    }
    return false;
  };

  const renderCategorySkeletons = (count = 5): React.ReactNode => (
    <>
      <li key="all-products-skeleton" className="category-skeleton">
        <Skeleton height={24} width="40%" />
      </li>
      {Array(count)
        .fill(0)
        .map((_, index) => (
          <li key={`category-skeleton-${index}`} className="category-skeleton">
            <div className="category-item">
              <Skeleton height={24} width={index % 2 === 0 ? "75%" : "60%"} />
            </div>
            {index % 3 === 0 && (
              <ul className="subcategories">
                <li className="category-skeleton">
                  <Skeleton height={20} width="50%" />
                </li>
                <li className="category-skeleton">
                  <Skeleton height={20} width="60%" />
                </li>
              </ul>
            )}
          </li>
        ))}
    </>
  );

  const renderTagSkeletons = (count = 8): React.ReactNode =>
    Array(count)
      .fill(0)
      .map((_, index) => (
        <Skeleton
          key={`tag-skeleton-${index}`}
          height={28}
          width={60}
          style={{
            marginRight: "8px",
            marginBottom: "8px",
            borderRadius: "3px",
          }}
        />
      ));

  const renderBrandSkeletons = (count = 4): React.ReactNode =>
    Array(count)
      .fill(0)
      .map((_, index) => (
        <Skeleton
          key={`brand-skeleton-${index}`}
          height={28}
          width={80}
          style={{
            marginRight: "8px",
            marginBottom: "8px",
            borderRadius: "3px",
          }}
        />
      ));

  const renderSearchSkeleton = (): React.ReactNode => (
    <div className="search-skeleton">
      <Skeleton height={36} width="100%" style={{ marginBottom: "8px" }} />
    </div>
  );

  const renderCategory = (category: CategoryWithChildren): React.ReactNode => {
    if (!hasCategoryProducts(category)) return null;
    const isExpanded =
      expandedCategories[category.id] || hasActiveChild(category);
    const validChildren = category.children
      ? category.children.filter(hasCategoryProducts)
      : [];
    const hasChildren = validChildren.length > 0;
    const isActive = isCategoryActive(category.id);
    return (
      <li key={category.id} className={hasChildren ? "has-children" : ""}>
        <div className="category-item">
          <div className="category-checkbox-wrapper">
            <input
              type="checkbox"
              id={`${idPrefix}-category-${category.id}`}
              checked={isActive}
              onChange={() => onCategoryClick(category.id)}
            />
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onCategoryClick(category.id);
              }}
              className={`category-link ${isActive ? "active" : ""}`}
            >
              {category.name} <span className="count">({category.count})</span>
            </a>
          </div>
          {hasChildren && (
            <button
              className={`toggle-subcategories ${isExpanded ? "expanded" : ""}`}
              onClick={() => toggleCategory(category.id)}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? t("filterSidebar.collapseSubcategories")
                  : t("filterSidebar.expandSubcategories")
              }
            >
              <span className="sr-only">
                {isExpanded ? "Collapse" : "Expand"}
              </span>
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d={isExpanded ? "M7 11l5-5 5 5H7z" : "M7 10l5 5 5-5H7z"}
                />
              </svg>
            </button>
          )}
        </div>
        {hasChildren && isExpanded && (
          <ul className="subcategories">
            {validChildren.map((child) => renderCategory(child))}
          </ul>
        )}
      </li>
    );
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSearchFilter(searchValue);
  };

  return (
    <aside className="product-filters-offcanvas">
      <div className="filter-offcanvas-header">
        <h3 id={`${idPrefix}-filter-offcanvas-title`}>
          {t("filterSidebar.title")}
        </h3>
        {onClose && (
          <button
            className="filter-close-button"
            onClick={onClose}
            aria-label={t("filterSidebar.closeAriaLabel")}
          >
            ×
          </button>
        )}
      </div>
      <div className="filter-offcanvas-content">
        <div className="filter-section">
          {isLoading ? (
            <Skeleton width={100} height={20} />
          ) : (
            <h3>{t("filterSidebar.brands")}</h3>
          )}
          <div className="product-brands">
            {isLoading ? (
              renderBrandSkeletons()
            ) : brands?.length > 0 ? (
              brands
                .filter((brand) => brand.count > 0)
                .map((brand) => (
                  <a
                    key={brand.id}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onBrandClick(brand.slug);
                    }}
                    className={`brand-link ${
                      activeFilters.brand.includes(brand.slug) ? "active" : ""
                    }`}
                  >
                    {brand.name}
                    <span className="count">({brand.count})</span>
                  </a>
                ))
            ) : (
              <div className="no-brands-message">
                {t("filterSidebar.noBrands")}
              </div>
            )}
          </div>
        </div>
        <div className="filter-section">
          {isLoading ? (
            <Skeleton width={120} height={20} />
          ) : (
            <h3>{t("productArchive.categories")}</h3>
          )}
          <ul className="product-categories hierarchical">
            {isLoading ? (
              renderCategorySkeletons()
            ) : (
              <>
                <li>
                  <div className="category-item">
                    <div className="category-checkbox-wrapper">
                      <input
                        type="checkbox"
                        id={`${idPrefix}-category-all`}
                        checked={activeFilters.category.length === 0}
                        onChange={() => {
                          if (activeFilters.category.length > 0)
                            onCategoryClick(null);
                        }}
                      />
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (activeFilters.category.length > 0)
                            onCategoryClick(null);
                        }}
                        className={`category-link ${
                          activeFilters.category.length === 0 ? "active" : ""
                        }`}
                      >
                        {t("productArchive.allProducts")}
                      </a>
                    </div>
                  </div>
                </li>
                {categoryTree.filter(hasCategoryProducts).map(renderCategory)}
              </>
            )}
          </ul>
        </div>
        <div className="filter-section">
          {isLoading ? (
            <Skeleton width={80} height={20} />
          ) : (
            <h3>{t("filterSidebar.tags")}</h3>
          )}
          <div className="product-tags">
            {isLoading
              ? renderTagSkeletons()
              : topTags.map((tag) => (
                  <a
                    key={tag.id}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onTagClick(tag.id);
                    }}
                    className={`tag-link ${
                      activeFilters.tag.includes(tag.id) ? "active" : ""
                    }`}
                  >
                    {tag.name}
                  </a>
                ))}
          </div>
        </div>
        <div className="filter-section">
          {isLoading ? (
            <Skeleton width={130} height={20} />
          ) : (
            <h3>{t("filterSidebar.searchProducts")}</h3>
          )}
          {isLoading ? (
            renderSearchSkeleton()
          ) : (
            <form className="search-form" onSubmit={handleSearchSubmit}>
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder={t("filterSidebar.searchPlaceholder")}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="search-input"
                />
                <div className="search-actions">
                  <button type="submit" className="search-btn">
                    {t("filterSidebar.search")}
                  </button>
                  {searchValue && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={() => {
                        setSearchValue("");
                        onSearchFilter("");
                      }}
                    >
                      {t("filterSidebar.clear")}
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </aside>
  );
};

export default FilterSidebar;
